/**
 * IssueGroupList — renders a list of issue groups with severity, title,
 * affected page count, optional change type indicator, and expandable remediation guidance.
 */

import { useState, useMemo } from "react";
import type {
  ReportIssueGroup,
  IssueGroupChangeSummary,
  IssueGroupChangeType,
} from "../api/product-types.js";
import { IssueRemediationSection } from "./issue-remediation-section.js";
import { EvidenceCard } from "./evidence-card.js";

// ── Filter constants ────────────────────────────────────────────────────────

const SEVERITY_OPTIONS = ["All", "Critical", "Serious", "Moderate", "Minor"] as const;
const WCAG_LEVEL_OPTIONS = ["All", "A", "AA", "AAA"] as const;

type SeverityFilter = (typeof SEVERITY_OPTIONS)[number];
type WcagLevelFilter = (typeof WCAG_LEVEL_OPTIONS)[number];

// ── Report issue groups (from report payload) ─────────────────────────────────

interface ReportIssueGroupListProps {
  groups: ReportIssueGroup[];
  showRemediation?: boolean; // Default true
  /** Pass page counts to improve the empty-state message. */
  pagesScanned?: number;
  pagesDiscovered?: number;
  /** Whether the current plan includes screenshot viewing. */
  screenshotsEnabled?: boolean;
}

const SEVERITY_CLASS: Record<string, string> = {
  critical: "issue-row--critical",
  serious: "issue-row--serious",
  moderate: "issue-row--moderate",
  minor: "issue-row--minor",
};

export function ReportIssueGroupList({
  groups,
  showRemediation = true,
  pagesScanned,
  pagesDiscovered,
  screenshotsEnabled = false,
}: ReportIssueGroupListProps) {
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>("All");
  const [wcagLevelFilter, setWcagLevelFilter] = useState<WcagLevelFilter>("All");

  const filteredGroups = useMemo(() => {
    if (groups.length === 0) return groups;
    return groups.filter((g) => {
      if (severityFilter !== "All" && g.severity.toLowerCase() !== severityFilter.toLowerCase()) {
        return false;
      }
      if (wcagLevelFilter !== "All" && (g.wcagLevel ?? "") !== wcagLevelFilter) {
        return false;
      }
      return true;
    });
  }, [groups, severityFilter, wcagLevelFilter]);

  if (groups.length === 0) {
    // Distinguish "clean" from "nothing scanned"
    if (pagesScanned === 0) {
      return (
        <p className="text-muted">
          No issues found — but no pages were successfully scanned, so this
          does not mean the site is accessible.
        </p>
      );
    }
    if (pagesScanned != null && pagesDiscovered != null && pagesScanned < pagesDiscovered) {
      return (
        <p className="text-muted">
          No automated issues found on the {pagesScanned} {pagesScanned === 1 ? "page" : "pages"} scanned.
          {" "}{pagesDiscovered - pagesScanned} {pagesDiscovered - pagesScanned === 1 ? "page was" : "pages were"} not tested.
          This is not a guarantee of full accessibility.
        </p>
      );
    }
    return (
      <p className="text-muted">
        No automated issues found. Note that automated tools only detect a
        subset of accessibility barriers — manual testing is still recommended.
      </p>
    );
  }

  return (
    <>
      <div className="issue-filter-bar" role="toolbar" aria-label="Filter issues">
        <div className="issue-filter-group">
          <span className="issue-filter-label">Severity:</span>
          {SEVERITY_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`issue-filter-pill${severityFilter === opt ? " issue-filter-pill--active" : ""}`}
              onClick={() => setSeverityFilter(opt)}
              aria-pressed={severityFilter === opt}
            >
              {opt}
            </button>
          ))}
        </div>
        <div className="issue-filter-group">
          <span className="issue-filter-label">WCAG Level:</span>
          {WCAG_LEVEL_OPTIONS.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`issue-filter-pill${wcagLevelFilter === opt ? " issue-filter-pill--active" : ""}`}
              onClick={() => setWcagLevelFilter(opt)}
              aria-pressed={wcagLevelFilter === opt}
            >
              {opt}
            </button>
          ))}
        </div>
        <span className="issue-filter-count">
          Showing {filteredGroups.length} of {groups.length} issues
        </span>
      </div>

      {filteredGroups.length === 0 ? (
        <p className="text-muted">No issues match current filters.</p>
      ) : (
        <ul className="issue-group-list" aria-label="Issue groups">
          {filteredGroups.map((g) => (
            <li
              key={g.id}
              className={`issue-row ${SEVERITY_CLASS[g.severity] ?? ""}`}
            >
              <div className="issue-row__head">
                <span className="issue-row__severity">{g.severity}</span>
                <span className="issue-row__title">{g.title}</span>
              </div>
              <div className="issue-row__meta">
                <span>{g.affectedPagesCount} {g.affectedPagesCount === 1 ? "page" : "pages"} affected</span>
                {g.sourceEngines.length > 0 && (
                  <span className="text-muted">
                    Found by {g.sourceEngines.join(", ")}
                  </span>
                )}
              </div>
              {g.description && <p className="issue-row__desc">{g.description}</p>}

              <EvidenceCard
                screenshot={g.screenshot}
                criterion={g.criterion}
                criterionTitle={g.criterionTitle}
                criterionWhyItMatters={g.criterionWhyItMatters}
                wcagLevel={g.wcagLevel ?? null}
                w3cUrl={g.w3cUrl}
                screenshotsEnabled={screenshotsEnabled}
              />

              {showRemediation && (
                <IssueRemediationSection issueKey={g.issueKey} />
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ── Recurring change summaries ────────────────────────────────────────────────

interface ChangeIssueGroupListProps {
  groups: IssueGroupChangeSummary[];
}

const CHANGE_CLASS: Record<IssueGroupChangeType, string> = {
  new: "issue-row--new",
  resolved: "issue-row--resolved",
  unchanged: "issue-row--unchanged",
};

const CHANGE_LABEL: Record<IssueGroupChangeType, string> = {
  new: "New",
  resolved: "Resolved",
  unchanged: "Unchanged",
};

export function ChangeIssueGroupList({ groups }: ChangeIssueGroupListProps) {
  if (groups.length === 0) {
    return <p className="text-muted">No issue group changes.</p>;
  }

  return (
    <ul className="issue-group-list" aria-label="Issue group changes">
      {groups.map((g) => (
        <li
          key={g.issueKey}
          className={`issue-row ${CHANGE_CLASS[g.changeType]}`}
        >
          <div className="issue-row__head">
            <span className="issue-row__severity">{g.severity}</span>
            <span className="issue-row__title">{g.title}</span>
            <span className={`change-badge change-badge--${g.changeType}`}>
              {CHANGE_LABEL[g.changeType]}
            </span>
          </div>
          <div className="issue-row__meta">
            {g.previousAffectedPages != null && (
              <span>Before: {g.previousAffectedPages} {g.previousAffectedPages === 1 ? "page" : "pages"}</span>
            )}
            {g.currentAffectedPages != null && (
              <span>After: {g.currentAffectedPages} {g.currentAffectedPages === 1 ? "page" : "pages"}</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
